import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, TextField } from '@material-ui/core';

const SampleDataViewCard = ({ samplePoints, samplesData, handleSampleData }) => {
  const [avgHeight, setAvgHeight] = useState(samplesData?.avgHeight || '');
  const [quality, setQuality] = useState(samplesData?.quality || '');

  const avgDBH = useMemo(() => {
    if (samplePoints?.length === 0) return 0;
    const sum = samplePoints.reduce((acc, num) => acc + (num.dbh || 0), 0);

    const result = sum / samplePoints.length;
    return isNaN(result) ? 0 : Number(result.toFixed(1));
  }, [samplePoints]);

  const avgTreeSize = useMemo(() => {
    const formFactor = 0.8;
    const height = parseFloat(avgHeight);
    if (samplePoints?.length === 0 || !avgDBH) return 0;
    const radiusInMeters = avgDBH / 100 / 2;

    const size = formFactor * Math.PI * radiusInMeters ** 2 * height;
    return isNaN(size) ? 0 : Number(size.toFixed(1));
  }, [samplePoints, avgHeight]);

  const totalVolume = useMemo(() => {
    const height = parseFloat(avgHeight);
    if (samplePoints?.length === 0 || !avgDBH) return 0;
    const radiusInMeters = avgDBH / 100 / 2;

    const volume = Math.PI * radiusInMeters ** 2 * height;
    return isNaN(volume) ? '--' : Number(volume.toFixed(1));
  }, [samplePoints, avgHeight]);

  useEffect(() => {
    // Don't save if no meaningful data
    if (!avgHeight && !quality) return;

    const debounce = setTimeout(() => {
      const payload = {
        sampleName: samplesData?.sampleName,
        id: samplesData?.id,
        dbh: avgDBH,
        avgTreeSize,
        totalVolume,
        avgHeight,
        quality,
      };

      const type = samplesData?.id ? 'update' : 'add';
      handleSampleData && handleSampleData(payload, type);
    }, 1000);

    return () => clearTimeout(debounce);
  }, [avgHeight, quality]); // ONLY these two - removes the infinite loop

  return (
    <Box
      display="grid"
      gridTemplateColumns="repeat(auto-fit, minmax(120px, 1fr))"
      gap={2}
      sx={{ marginBottom: 24, backgroundColor: '#E7EDFE', padding: 16, borderRadius: 8 }}>
      {/* DBH */}
      <Box>
        <Typography variant="caption" color="text.secondary">
          DBH
        </Typography>
        <Typography fontWeight={500}>{avgDBH}</Typography>
      </Box>

      {/* Height (editable) */}
      <Box sx={{ marginRight: 24 }}>
        <Typography variant="caption" color="text.secondary">
          Height
        </Typography>
        <TextField
          variant="outlined"
          type="number"
          size="small"
          fullWidth
          value={avgHeight}
          onChange={e => {
            const val = e.target.value;
            setAvgHeight(val === '' || isNaN(val) ? '' : Number(val));
          }}
          inputProps={{ step: 0.1, min: 0 }}
        />
      </Box>

      {/* Size */}
      <Box>
        <Typography variant="caption" color="text.secondary">
          Size
        </Typography>
        <Typography fontWeight={500}>{avgTreeSize}</Typography>
      </Box>

      {/* Volume */}
      <Box>
        <Typography variant="caption" color="text.secondary">
          Volume
        </Typography>
        <Typography fontWeight={500}>{totalVolume}</Typography>
      </Box>

      {/* Quality (editable) */}
      <Box>
        <Typography variant="caption" color="text.secondary">
          Quality
        </Typography>
        <TextField variant="outlined" size="small" fullWidth value={quality} onChange={e => setQuality(e.target.value)} />
      </Box>
    </Box>
  );
};

export default SampleDataViewCard;
